import React, { useState, useEffect } from 'react';
import { XMarkIcon, LinkIcon, ClockIcon, CheckIcon, ClipboardDocumentIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import api from '../services/api';
import { useApp } from '../hooks/useApp';

function ShareModal({ isOpen, onClose, jobId, title, subscriptionInfo = null }) {
  const { user } = useApp();
  const [shareUrl, setShareUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [expiresIn, setExpiresIn] = useState('0'); // 0 = never expires
  const [shareMode, setShareMode] = useState('preview'); // Default based on subscription
  const [selectedContent, setSelectedContent] = useState([
    'actions',
    'executive_summary',
    'job_metadata'
  ]);

  // Use subscription info from parent component (from useActions hook)
  const hasPaidSubscription = subscriptionInfo ? subscriptionInfo.hasFullAccess : false;

  const shareModeOptions = [
    {
      value: 'full',
      label: 'Full Access',
      description: !hasPaidSubscription 
        ? 'Available with paid subscription - Recipients see all content immediately (for clients/team members)'
        : 'Recipients see all content immediately (for clients/team members)',
      icon: !hasPaidSubscription ? '🔒' : '🔓',
      disabled: !hasPaidSubscription
    },
    {
      value: 'preview',
      label: 'Preview Mode',
      description: 'Show first 8 actions + signup prompt. Recipients must purchase subscription to see full content, even if they have an account.',
      icon: '👀',
      disabled: false
    }
  ];

  const availableContent = [
    { name: 'actions', label: 'SEO Actions & Recommendations', description: 'Actionable SEO tasks and improvements' },
    { name: 'executive_summary', label: 'Executive Summary', description: 'High-level analysis overview' },
    { name: 'job_metadata', label: 'Analysis Details', description: 'Job information and timing' },
    { name: 'progress_tracking', label: 'Progress Tracking', description: 'Task completion status (coming soon)', disabled: true }
  ];

  useEffect(() => {
    if (!isOpen) {
      // Reset state when modal closes
      setShareUrl('');
      setError('');
      setCopied(false);
      // Set default share mode based on subscription info
      setShareMode(hasPaidSubscription ? 'full' : 'preview');
    }
  }, [isOpen, hasPaidSubscription]);

  const handleCreateShare = async () => {
    setLoading(true);
    setError('');
    
    console.log('ShareModal: Creating share with jobId:', jobId);
    console.log('ShareModal: SubscriptionInfo:', subscriptionInfo, 'hasPaidSubscription:', hasPaidSubscription);
    
    if (!jobId) {
      setError('Job ID is required to create a share link');
      setLoading(false);
      return;
    }
    
    try {
      const response = await api.post('/share/create', {
        jobId,
        title: title || `SEO Analysis Dashboard`,
        description: `Shared SEO analysis dashboard with actionable recommendations`,
        sharedContent: selectedContent,
        shareMode: shareMode,
        expiresIn: expiresIn === '0' ? null : parseInt(expiresIn)
      });

      if (response.success) {
        setShareUrl(response.data.shareUrl);
      } else {
        // Handle sharing restriction errors specifically
        if (response.code === 'SHARING_RESTRICTED') {
          setError(`${response.error} Please upgrade to a paid plan for full access sharing.`);
        } else {
          setError(response.error || 'Failed to create share link');
        }
      }
    } catch (err) {
      // Handle HTTP error responses
      if (err.response?.data?.code === 'SHARING_RESTRICTED') {
        setError(`${err.response.data.error} Please upgrade to a paid plan for full access sharing.`);
      } else {
        setError(err.message || err.response?.data?.error || 'Failed to create share link');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      setError('Failed to copy link to clipboard');
    }
  };

  const handleContentToggle = (contentName) => {
    setSelectedContent(prev => {
      if (prev.includes(contentName)) {
        return prev.filter(c => c !== contentName);
      } else {
        return [...prev, contentName];
      }
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-card rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">Share Analysis Dashboard</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-muted-foreground"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {!shareUrl ? (
            <>
              <div className="mb-4">
                <label className="block text-sm font-medium text-card-foreground mb-2">
                  Sharing Mode
                </label>
                <div className="space-y-2 mb-4">
                  {shareModeOptions.map(mode => (
                    <label key={mode.value} className={`flex items-start ${mode.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                      <input
                        type="radio"
                        name="shareMode"
                        value={mode.value}
                        checked={shareMode === mode.value}
                        onChange={(e) => setShareMode(e.target.value)}
                        disabled={mode.disabled}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-border mt-0.5 disabled:cursor-not-allowed"
                      />
                      <div className="ml-3">
                        <span className={`text-sm font-medium ${mode.disabled ? 'text-gray-400' : 'text-card-foreground'}`}>
                          {mode.icon} {mode.label}
                          {mode.disabled && <LockClosedIcon className="h-4 w-4 inline ml-1 text-gray-400" />}
                        </span>
                        <p className={`text-xs ${mode.disabled ? 'text-gray-400' : 'text-muted-foreground'}`}>
                          {mode.description}
                        </p>
                      </div>
                    </label>
                  ))}
                </div>
                {!hasPaidSubscription && (
                  <div className="mb-4 p-3 bg-accent border border-blue-200 rounded-md">
                    <div className="flex items-start">
                      <LockClosedIcon className="h-5 w-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-blue-800">
                          Upgrade for Full Access Sharing
                        </p>
                        <p className="text-xs text-blue-600 mt-1">
                          Paid subscribers can create full access links that show all content immediately to recipients. 
                          <a href="/pricing" className="underline hover:text-blue-800 ml-1">
                            View plans →
                          </a>
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-card-foreground mb-2">
                  Content to Share
                </label>
                <p className="text-xs text-muted-foreground mb-3">
                  Recipients will see a professional dashboard with the selected content sections.
                </p>
                <div className="space-y-3">
                  {availableContent.map(content => (
                    <label key={content.name} className={`flex items-start ${content.disabled ? 'opacity-50' : ''}`}>
                      <input
                        type="checkbox"
                        checked={selectedContent.includes(content.name)}
                        onChange={() => handleContentToggle(content.name)}
                        disabled={content.disabled}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-border rounded mt-0.5"
                      />
                      <div className="ml-3">
                        <span className="text-sm font-medium text-card-foreground">{content.label}</span>
                        <p className="text-xs text-muted-foreground">{content.description}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-card-foreground mb-2">
                  Link Expiration
                </label>
                <select
                  value={expiresIn}
                  onChange={(e) => setExpiresIn(e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="0">Never expires</option>
                  <option value="24">24 hours</option>
                  <option value="72">3 days</option>
                  <option value="168">7 days</option>
                  <option value="720">30 days</option>
                </select>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              <button
                onClick={handleCreateShare}
                disabled={loading || selectedContent.length === 0}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Creating...
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    <LinkIcon className="h-5 w-5 mr-2" />
                    Create Dashboard Link
                  </span>
                )}
              </button>
            </>
          ) : (
            <>
              <div className="mb-4">
                <p className="text-sm text-muted-foreground mb-3">
                  Your shareable dashboard link has been created! Anyone with this link can view the selected content in a professional dashboard format.
                </p>
                
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={shareUrl}
                    readOnly
                    className="flex-1 px-3 py-2 border border-border rounded-md bg-background text-sm"
                  />
                  <button
                    onClick={handleCopyLink}
                    className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                  >
                    {copied ? (
                      <CheckIcon className="h-5 w-5 text-green-600" />
                    ) : (
                      <ClipboardDocumentIcon className="h-5 w-5 text-muted-foreground" />
                    )}
                  </button>
                </div>
                
                {copied && (
                  <p className="text-sm text-green-600 mt-2">Link copied to clipboard!</p>
                )}
              </div>

              {expiresIn !== '0' && (
                <div className="flex items-center text-sm text-muted-foreground mb-4">
                  <ClockIcon className="h-4 w-4 mr-1" />
                  <span>Expires in {expiresIn} hours</span>
                </div>
              )}

              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShareUrl('');
                    setError('');
                  }}
                  className="flex-1 px-4 py-2 bg-gray-100 text-card-foreground rounded-md hover:bg-gray-200 transition-colors"
                >
                  Create Another
                </button>
                <button
                  onClick={onClose}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  Done
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ShareModal;